// ai.js (OpenAI version)
import OpenAI from 'openai';

const apiKey = process.env.REACT_APP_OPENAI;
// Optional: let model be configurable; fall back to a sensible default
const modelName = process.env.REACT_APP_OPENAI_MODEL || 'gpt-4o-mini';

// ⚠️ If this runs in the browser, your key will be exposed.
// Prefer calling these helpers from your server (Node). If you *must* run
// in the browser during prototyping, set dangerouslyAllowBrowser: true.
export const client = new OpenAI({
  apiKey,
  dangerouslyAllowBrowser: true, // remove in production; proxy via your backend instead
});

// --- Helpers ---
const cleanJsonString = (text) => {
  return (text || '')
    .replace(/```json/g, '')
    .replace(/```/g, '')
    .replace(/\.\.\./g, '')
    .replace(/^Here is the report in JSON format:/i, '')
    .trim();
};

// Prefer JSON-mode when you want strict JSON back
async function chatJSON(messages) {
  const res = await client.chat.completions.create({
    model: modelName,
    messages,
    // JSON mode (safer than cleaning code fences)
    response_format: { type: 'json_object' },
  });
  return res.choices?.[0]?.message?.content || '';
}

async function chatText(messages) {
  const res = await client.chat.completions.create({
    model: modelName,
    messages,
  });
  return res.choices?.[0]?.message?.content || '';
}

// --- Public wrappers (API-compatible with your Azure version) ---

export const LLMTextToJSON = async (processedInput) => {
  try {
    const raw = await chatJSON([{ role: 'user', content: processedInput }]);
    // With response_format: json_object, this should already be pure JSON:
    return JSON.parse(raw);
  } catch (error) {
    console.error('OpenAI JSON error:', error);
    // Fallback: attempt to clean fences if provider ever returns text
    try { return JSON.parse(cleanJsonString(error?.raw || '')); } catch { return null; }
  }
};

export const LLMTextToText = async (processedInput) => {
  try {
    return await chatText([{ role: 'user', content: processedInput }]);
  } catch (error) {
    console.error('OpenAI text error:', error);
    return null;
  }
};

export const writeNotes = async (instruction, input) => {
  return await LLMTextToText(`${instruction}\n${input}`);
};

export const sortingText = async (input) => {
  return await LLMTextToJSON(input);
};

export const AITextToObject = async (instruction, input) => {
  try {
    const messages = [
      { role: 'system', content: instruction },
      { role: 'user', content: `User: ${input}` },
    ];
    const raw = await chatJSON(messages);
    return JSON.parse(raw);
  } catch (error) {
    console.error('OpenAI Object error:', error);
    // Fallback cleaning if ever needed
    try { return JSON.parse(cleanJsonString(error?.raw || '')); } catch { return null; }
  }
};

export const AITextToText = async (instruction, input, initData) => {
  if (!input) return null;
  const messages = [
    { role: 'system', content: instruction },
    { role: 'user', content: input },
  ];
  if (initData) {
    messages.push({ role: 'user', content: JSON.stringify(initData) });
  }
  try {
    return await chatText(messages);
  } catch (error) {
    console.error('OpenAI AITextToText error:', error);
    return null;
  }
};

export const AITextToObjectwithData = async (instruction, input, initData) => {
  try {
    const messages = [
      { role: 'system', content: instruction },
      { role: 'user', content: input },
      { role: 'user', content: JSON.stringify(initData) },
    ];
    const raw = await chatJSON(messages);
    return JSON.parse(raw);
  } catch (error) {
    console.error('OpenAI withData error:', error);
    try { return JSON.parse(cleanJsonString(error?.raw || '')); } catch { return null; }
  }
};

export const AITextToObjectwithDataAndRef = async (instruction, input, initData, references) => {
  try {
    const messages = [{ role: 'system', content: instruction }];
    if (input) messages.push({ role: 'user', content: input });
    if (initData) messages.push({ role: 'user', content: JSON.stringify(initData) });
    if (references) messages.push({ role: 'user', content: JSON.stringify(references) });

    const raw = await chatJSON(messages);
    return JSON.parse(raw);
  } catch (error) {
    console.error('OpenAI withDataAndRef error:', error);
    try { return JSON.parse(cleanJsonString(error?.raw || '')); } catch { return null; }
  }
};
